import { useCallback, useMemo, useRef } from 'react';
import { AdviseSupplierScoring, SupplierRating } from '../../../types/advise';
import { fetchAdviseSupplierScoring, updateAdviseSupplierRating } from '../../../Api/Advise/SupplierScoring';
import { isNotFoundRefusal } from '../../../utils/refusalMessage';
import { EngagementSupplierScoring } from '../../Compounds/Advise/SideBySideView';
import { usePendingSaves } from './usePendingSaves';
import { useLoadAfterSaves } from './useLoadAfterSaves';

type PendingSaves = ReturnType<typeof usePendingSaves>;

type RatedFunction = Pick<SupplierRating, 'supplier_id' | 'function_id'>;

const sameFunction = (a: RatedFunction, b: RatedFunction) =>
  a.supplier_id === b.supplier_id && a.function_id === b.function_id;

const withRating = (scoring: AdviseSupplierScoring, saved: SupplierRating): AdviseSupplierScoring => ({
  ...scoring,
  ratings: [...scoring.ratings.filter((row) => !sameFunction(row, saved)), saved]
});

// Who wrote a note, and when, comes back with the save; taken only while the rating still shows what was sent.
const withSavedRating = (scoring: AdviseSupplierScoring, saved: SupplierRating): AdviseSupplierScoring => {
  const shown = scoring.ratings.find((row) => sameFunction(row, saved));
  return shown?.rating === saved.rating ? withRating(scoring, saved) : scoring;
};

/** UPSTACK's function ratings of the shortlist, loaded when the tab asks, with each change saved in the background. */
export const useEngagementSupplierScoring = (
  engagementId: string | null,
  canEdit: boolean,
  { debouncedSave, flushSaves }: PendingSaves
): EngagementSupplierScoring => {
  const {
    data: scoring,
    failed,
    refresh,
    showEdit,
    saveOrReload
  } = useLoadAfterSaves(engagementId, fetchAdviseSupplierScoring, flushSaves);
  // The queue sends only the newest save for a function, so a note it hasn't confirmed yet goes with every later save
  // that leaves the note out, and an answer from before that note can't put the stored one back on screen.
  const unsavedNotes = useRef<Record<string, { note: string | null }>>({});

  // A supplier dropped from the shortlist since answers 404, so its save is dropped and the tab reloaded. A note left
  // out keeps the stored one, so the answer brings whatever note is there now.
  const rate = useCallback(
    (supplierId: string, functionId: string, rating: number, note?: string | null) => {
      if (!canEdit || !engagementId) return;
      const rated = { supplier_id: supplierId, function_id: functionId };
      showEdit((prev) => {
        const kept = prev.ratings.find((row) => sameFunction(row, rated));
        const noteFields =
          note === undefined
            ? { note: kept?.note ?? null, noted_by_name: kept?.noted_by_name ?? null, noted_at: kept?.noted_at ?? null }
            : { note, noted_by_name: null, noted_at: null };
        return withRating(prev, { ...rated, rating, ...noteFields });
      });
      const key = `supplierRating:${engagementId}:${supplierId}:${functionId}`;
      if (note !== undefined) unsavedNotes.current[key] = { note };
      const carried = unsavedNotes.current[key];
      debouncedSave(
        key,
        saveOrReload(async () => {
          const result = await updateAdviseSupplierRating(engagementId, supplierId, functionId, rating, carried?.note);
          const { data, error } = result;
          const waiting = unsavedNotes.current[key];
          // Cleared means an earlier save with this same note already answered, so this answer is still the newest.
          if ((waiting ?? carried) !== carried) return result;
          // A 404 drops the save, and a note kept past it would ride along once the supplier is shortlisted again.
          if (waiting && (data || isNotFoundRefusal(error))) delete unsavedNotes.current[key];
          if (data) showEdit((prev) => withSavedRating(prev, data));
          return result;
        })
      );
    },
    [canEdit, engagementId, debouncedSave, saveOrReload, showEdit]
  );

  return useMemo(() => ({ scoring, failed, refresh, rate }), [scoring, failed, refresh, rate]);
};
